import { Children, type CSSProperties, type ReactNode, useLayoutEffect, useRef } from "react";

import { cn } from "@/lib/utils";

const MASONRY_ROW_HEIGHT_PX = 1;
export const MASONRY_GAP_PX = 12;

export function getMasonryRowSpan(
  itemHeight: number,
  gap = MASONRY_GAP_PX,
  rowHeight = MASONRY_ROW_HEIGHT_PX,
) {
  if (!Number.isFinite(itemHeight) || itemHeight <= 0) {
    return 1;
  }

  return Math.max(1, Math.ceil((itemHeight + gap) / rowHeight));
}

export function MasonryGrid({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div
      className={cn(
        "grid grid-flow-row-dense gap-x-3 [--masonry-column-width:17rem] sm:[--masonry-column-width:18rem] 2xl:[--masonry-column-width:19rem]",
        className,
      )}
      style={
        {
          gridAutoRows: `${MASONRY_ROW_HEIGHT_PX}px`,
          gridTemplateColumns:
            "repeat(auto-fill, minmax(min(100%, var(--masonry-column-width)), 1fr))",
        } as CSSProperties
      }
    >
      {Children.map(children, (child) =>
        child === null || child === undefined ? null : <MasonryGridItem>{child}</MasonryGridItem>,
      )}
    </div>
  );
}

function MasonryGridItem({ children }: { children: ReactNode }) {
  const itemRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const item = itemRef.current;
    const content = contentRef.current;

    if (!item || !content) {
      return;
    }

    const updateSpan = () => {
      const nextSpan = getMasonryRowSpan(content.getBoundingClientRect().height);
      const nextGridRowEnd = `span ${nextSpan}`;

      if (item.style.gridRowEnd !== nextGridRowEnd) {
        item.style.gridRowEnd = nextGridRowEnd;
      }
    };

    updateSpan();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const observer = new ResizeObserver(updateSpan);
    observer.observe(content);

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={itemRef} className="min-w-0 self-start">
      <div ref={contentRef}>{children}</div>
    </div>
  );
}
